import type { ScheduleDto } from "@repo/data-store";
import { Building, CalendarDays, Cpu, User } from "lucide-react";

/** Actor type extracted from schedule */
export type ActorType = "Practitioner" | "Location" | "Device" | "Other";

export interface ActorInfo {
  type: ActorType;
  name: string;
  ref: string;
}

/** Extract actor info from schedule. Display names are resolved at the data layer via Practitioner/Location/Device collections. */
export function getActorInfo(schedule: ScheduleDto): ActorInfo {
  const { type, id, display } = schedule.actor;
  const ref = `${type}/${id}`;
  const name = display ?? id ?? "Unknown";

  if (type === "Practitioner" || type === "Location" || type === "Device") {
    return { type, name, ref };
  }
  return { type: "Other", name, ref };
}

/** Group schedules by actor type in preferred order */
export function groupSchedulesByActorType(schedules: ScheduleDto[]): Array<[ActorType, ScheduleDto[]]> {
  const byType: Record<ActorType, ScheduleDto[]> = {
    Practitioner: [],
    Location: [],
    Device: [],
    Other: [],
  };
  for (const schedule of schedules) {
    const { type } = getActorInfo(schedule);
    byType[type].push(schedule);
  }
  // Return in preferred order, filtering empty groups
  const order: ActorType[] = ["Practitioner", "Location", "Device", "Other"];
  return order.filter((type) => byType[type].length > 0).map((type) => [type, byType[type]]);
}

/** Filter schedules to only those with a Practitioner actor */
export function filterPractitionerSchedules(schedules: ScheduleDto[]): ScheduleDto[] {
  return schedules.filter((s) => s.actor.type === "Practitioner");
}

/**
 * Resolve schedule IDs belonging to a specific practitioner.
 * Returns only schedules whose actor is `Practitioner/<practitionerId>`.
 * Never falls back to Device or Location schedules.
 * Returns empty array when practitionerId is empty or no match found.
 */
export function resolveConsultPractitionerScheduleIds(schedules: ScheduleDto[], practitionerId: string): string[] {
  if (!practitionerId) return [];
  return schedules
    .filter((s) => s.actor.type === "Practitioner" && s.actor.id === practitionerId)
    .map((s) => s.scheduleId);
}

/** Get icon for actor type */
export function getActorIcon(type: ActorType) {
  switch (type) {
    case "Practitioner":
      return User;
    case "Location":
      return Building;
    case "Device":
      return Cpu;
    default:
      return CalendarDays;
  }
}
